// src/services/creditNoteService.js
/**
 * Credit Note Service - Handles all credit note-related API communication with backend
 * Backend API Base URL: Production VPS via config.js
 * 
 * Credit Notes (Notas de Crédito) are used to cancel or partially refund invoices
 * according to AFIP regulations in Argentina.
 */

import axios from 'axios';


// Import the NODE backend URL from config - VPS
const getAPIBaseURL = () => {
    try {
        // Use the NODE_BACKEND_API_URL which includes /apitest
        const configURL = "https://horizontepyme.proyectoshorizontales.com/apitest";
        return configURL;
    } catch (error) {
        console.warn('Could not load config, falling back to localhost:', error);
        return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8096/apitest';
    }
};


/*
// Import the NODE backend URL from config - CIA
const getAPIBaseURL = () => {
    try {
        // Use the NODE_BACKEND_API_URL which includes /apitest
        const configURL = "http://192.168.0.4:8096/apitest";
        return configURL;
    } catch (error) {
        console.warn('Could not load config, falling back to localhost:', error);
        return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8096/apitest';
    }
};
*/

const CREDIT_NOTE_API_URL = `${getAPIBaseURL()}/credit-notes`;

// Note: Authentication is handled automatically by axios interceptors in AuthProvider
// No need for manual token management

/**
 * Create a credit note for a specific invoice
 * @param {number} invoiceId - ID of the invoice to credit
 * @param {Object} options - Credit note options
 * @param {number} options.amount - Amount to credit (optional, defaults to full invoice amount)
 * @param {string} options.reason - Reason for credit note (optional)
 * @param {Array} options.items - Specific items to credit (optional, for partial credits)
 * @returns {Promise<Object>} Credit note creation response with CAE, reference, etc.
 */
export const createCreditNote = async (invoiceId, options = {}) => {
    try {
        // Validate required data
        if (!invoiceId) {
            throw new Error('Invoice ID is required to create a credit note');
        }
        
        // Ensure invoiceId is a number
        const parsedInvoiceId = typeof invoiceId === 'string' ? parseInt(invoiceId, 10) : invoiceId;
        
        // Build payload based on V2 API spec
        const payload = {
            originalInvoiceId: parsedInvoiceId,
            reason: options.reason || 'Anulación de comprobante',
        };
        
        // Add cancellationType and items for partial cancellations
        if (options.cancellationType === 'partial' && options.items) {
            payload.cancellationType = 'partial';
            payload.items = options.items; // Array of { invoiceItemId, quantity }
        } else if (options.cancellationType === 'full') {
            payload.cancellationType = 'full';
        }
        // If no cancellationType specified, backend defaults to 'full' for backward compatibility
        
        console.log('[CreditNote] Creating credit note with payload:', payload);
        console.log('=== COMPLETE CREDIT NOTE PAYLOAD ===');
        console.log('URL:', `${CREDIT_NOTE_API_URL}/create`);
        console.log('Payload structure:');
        console.log('- originalInvoiceId:', payload.originalInvoiceId);
        console.log('- amount:', payload.amount);
        console.log('- reason:', payload.reason);
        console.log('- items:', payload.items);
        console.log('- partial:', payload.partial);
        console.log('=== FULL JSON PAYLOAD ===');
        console.log(JSON.stringify(payload, null, 2));
        console.log('===============================');
        
        try {
            // Use axios with 60-second timeout (TusFacturas instant API may take time)
            // Auth header is automatically added by axios interceptors in AuthProvider
            const response = await axios.post(
                `${CREDIT_NOTE_API_URL}/create`,
                payload,
                { 
                    timeout: 60000, // 60 seconds
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            console.log('[CreditNote] Response status:', response.status);
            console.log('[CreditNote] Response headers:', response.headers);
            
            const result = response.data;
            console.log('=== COMPLETE CREDIT NOTE RESPONSE ===');
            console.log('Parsed result structure:', JSON.stringify(result, null, 2));
            console.log('=== RESPONSE ANALYSIS ===');
            
            // Transform backend response (snake_case) to frontend format (camelCase)
            if (result.success && result.creditNote) {
                result.data = {
                    id: result.creditNote.id,
                    numeroComprobante: result.creditNote.numero_comprobante,
                    status: result.creditNote.status,
                    cae: result.creditNote.cae?.trim(),
                    caeVencimiento: result.creditNote.cae_vencimiento,
                    pdfUrl: result.creditNote.pdf_url,
                    qrCode: result.creditNote.qr_code?.trim()
                };
                console.log('- Transformed data:', result.data);
                console.log('- numeroComprobante:', result.data.numeroComprobante);
                console.log('- cae:', result.data.cae);
                console.log('- status:', result.data.status);
            }
            console.log('===============================');
            return result;
        } catch (axiosError) {
            // Handle axios timeout
            if (axiosError.code === 'ECONNABORTED') {
                console.error('[CreditNote] Timeout after 60 seconds');
                throw new Error('La generación de la nota de crédito está tomando más tiempo del esperado. El proceso puede continuar en segundo plano - por favor verifique en unos minutos.');
            }
            
            // Handle API error responses
            if (axiosError.response) {
                const errorMessage = axiosError.response.data?.error || 
                                   axiosError.response.data?.message || 
                                   `HTTP ${axiosError.response.status}: ${axiosError.response.statusText}`;
                throw new Error(errorMessage);
            }
            
            throw axiosError;
        }
    } catch (error) {
        console.error('[CreditNote] Error creating credit note:', error);
        throw new Error(`Credit note creation failed: ${error.message}`);
    }
};

/**
 * Get credit note history with pagination and filtering
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 20)
 * @param {string} options.id - Filter by credit note ID (optional)
 * @param {string} options.numeroComprobante - Filter by credit note number (optional)
 * @param {string} options.invoiceNumeroComprobante - Filter by original invoice number (optional)
 * @param {string} options.clienteRazonSocial - Filter by client name (optional)
 * @param {string} options.tipoComprobante - Filter by voucher type (optional)
 * @param {string} options.cancellationType - Filter by cancellation type: "full" or "partial" (optional)
 * @param {string} options.dateFrom - Filter from date YYYY-MM-DD (optional)
 * @param {string} options.dateTo - Filter to date YYYY-MM-DD (optional)
 * @returns {Promise<Object>} Credit note history response with pagination and summary
 */
export const getCreditNoteHistory = async (options = {}) => {
    try {
        const {
            page = 1,
            limit = 20,
            id,
            numeroComprobante,
            invoiceNumeroComprobante,
            clienteRazonSocial,
            tipoComprobante,
            cancellationType,
            dateFrom,
            dateTo
        } = options;
        
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        });
        
        if (id) params.append('id', id);
        if (numeroComprobante) params.append('numeroComprobante', numeroComprobante);
        if (invoiceNumeroComprobante) params.append('invoiceNumeroComprobante', invoiceNumeroComprobante);
        if (clienteRazonSocial) params.append('clienteRazonSocial', clienteRazonSocial);
        if (tipoComprobante) params.append('tipoComprobante', tipoComprobante);
        if (cancellationType) params.append('cancellationType', cancellationType);
        if (dateFrom) params.append('dateFrom', dateFrom);
        if (dateTo) params.append('dateTo', dateTo);
        
        const response = await axios.get(`${CREDIT_NOTE_API_URL}?${params.toString()}`);
        return response.data;
    } catch (error) {
        console.error('[CreditNote] Error fetching credit note history:', error);
        throw new Error(`Failed to fetch credit note history: ${error.message}`);
    }
};

/**
 * Get specific credit note by ID
 * @param {number} creditNoteId - Credit note ID
 * @returns {Promise<Object>} Credit note details with items
 */
export const getCreditNote = async (creditNoteId) => {
    try {
        if (!creditNoteId) {
            throw new Error('Credit note ID is required');
        }
        
        const response = await axios.get(`${CREDIT_NOTE_API_URL}/${creditNoteId}`);
        return response.data;
    } catch (error) {
        console.error('[CreditNote] Error fetching credit note:', error);
        throw new Error(`Failed to fetch credit note: ${error.message}`);
    }
};

/**
 * Get credit notes associated with a specific invoice
 * @param {number} invoiceId - Invoice ID
 * @returns {Promise<Object>} List of credit notes for the invoice
 */
export const getCreditNotesByInvoice = async (invoiceId) => {
    try {
        if (!invoiceId) {
            throw new Error('Invoice ID is required');
        }
        
        const response = await axios.get(`${CREDIT_NOTE_API_URL}/by-invoice/${invoiceId}`);
        return response.data;
    } catch (error) {
        console.error('[CreditNote] Error fetching credit notes by invoice:', error);
        throw new Error(`Failed to fetch credit notes for invoice: ${error.message}`);
    }
};

/**
 * Retry failed credit note generation
 * @param {number} creditNoteId - Credit note ID to retry
 * @returns {Promise<Object>} Retry response
 */
export const retryCreditNote = async (creditNoteId) => {
    try {
        if (!creditNoteId) {
            throw new Error('Credit note ID is required');
        }
        
        const response = await axios.post(`${CREDIT_NOTE_API_URL}/retry/${creditNoteId}`);
        return response.data;
    } catch (error) {
        console.error('[CreditNote] Error retrying credit note:', error);
        throw new Error(`Failed to retry credit note: ${error.message}`);
    }
};

/**
 * Download credit note PDF
 * @param {number} creditNoteId - Credit note ID
 * @returns {Promise<string>} PDF URL for download/viewing
 */
export const getCreditNotePDF = async (creditNoteId) => {
    try {
        if (!creditNoteId) {
            throw new Error('Credit note ID is required');
        }
        
        const response = await axios.get(`${CREDIT_NOTE_API_URL}/${creditNoteId}/pdf`);
        return response.data.data.pdfUrl;
    } catch (error) {
        console.error('[CreditNote] Error getting credit note PDF:', error);
        throw new Error(`Failed to get credit note PDF: ${error.message}`);
    }
};

/**
 * Get creditable items from an invoice (items available for creating credit notes)
 * NEW ENDPOINT for V2 API - supports partial credit notes
 * @param {number} invoiceId - Invoice ID
 * @returns {Promise<Object>} Creditable items with remaining quantities
 */
export const getCreditableItems = async (invoiceId) => {
    try {
        if (!invoiceId) {
            throw new Error('Invoice ID is required');
        }
        
        const response = await axios.get(`${CREDIT_NOTE_API_URL}/creditable-items/${invoiceId}`);
        return response.data;
    } catch (error) {
        console.error('[CreditNote] Error fetching creditable items:', error);
        throw new Error(`Failed to fetch creditable items: ${error.message}`);
    }
};

/**
 * Check if an invoice can have a credit note created
 * @param {number} invoiceId - Invoice ID to check
 * @param {string} cancellationType - 'full' (default) or 'partial'. A partial credit
 *   note can coexist with an existing approved note, a full one cannot.
 * @returns {Promise<Object>} { success, canCreate, reason, details }
 */
export const canCreateCreditNote = async (invoiceId, cancellationType = 'full') => {
    try {
        if (!invoiceId) {
            throw new Error('Invoice ID is required');
        }
        
        const response = await axios.get(`${CREDIT_NOTE_API_URL}/can-credit/${invoiceId}`, {
            params: { cancellationType }
        });
        return response.data;
    } catch (error) {
        console.error('[CreditNote] Error checking credit note eligibility:', error);
        throw new Error(`Failed to check credit note eligibility: ${error.message}`);
    }
};

/**
 * Credit note status constants
 */
export const CREDIT_NOTE_STATUS = {
    DRAFT: 'draft',
    PENDING: 'pending',
    APPROVED: 'approved',
    ERROR: 'error',
    CANCELLED: 'cancelled'
};

/**
 * Export all functions for use in components
 */
export default {
    createCreditNote,
    getCreditNoteHistory,
    getCreditNote,
    getCreditNotesByInvoice,
    getCreditableItems,
    retryCreditNote,
    getCreditNotePDF,
    canCreateCreditNote,
    CREDIT_NOTE_STATUS
};
